// frontend/src/features/onboarding/steps/SourceSelectionStep.tsx
import { useState, useEffect, useRef } from 'react'
import { sources as sourcesApi, preferences as preferencesApi, settings as settingsApi } from '@/lib/api'
import { ArrowRight, ArrowLeft, Lock, ChevronUp, ChevronDown } from 'lucide-react'

interface SourceSelectionStepProps {
    eyebrow: string
    accentClass: string
    btnClass: string
    onNext: () => void
    onPrev: () => void
}

const CATEGORIES = [
    { key: 'technology', label: 'Technology', color: '#6600FF' },
    { key: 'finance', label: 'Finance', color: '#378ADD' },
    { key: 'world_news', label: 'World News', color: '#00c896' },
    { key: 'science', label: 'Science', color: '#9966ff' },
    { key: 'business', label: 'Business', color: '#00FFC0' },
    { key: 'politics', label: 'Politics', color: '#FF9A3C' },
    { key: 'health', label: 'Health', color: '#FF6B6B' },
    { key: 'sports', label: 'Sports', color: '#4CAF50' },
    { key: 'entertainment', label: 'Entertainment', color: '#FF12C7' },
    { key: 'general', label: 'General', color: '#888' },
]

const DEFAULT_CATEGORIES = ['technology', 'business', 'world_news']

function useIsMobile() {
    const [mobile, setMobile] = useState(() => window.innerWidth < 640)
    useEffect(() => {
        const h = () => setMobile(window.innerWidth < 640)
        window.addEventListener('resize', h)
        return () => window.removeEventListener('resize', h)
    }, [])
    return mobile
}

export function SourceSelectionStep({ eyebrow, accentClass, btnClass, onNext, onPrev }: SourceSelectionStepProps) {
    const isMobile = useIsMobile()
    const [isLoading, setIsLoading] = useState(true)
    const [isSaving, setIsSaving] = useState(false)
    const [selectedCategories, setSelectedCategories] = useState<Set<string>>(new Set(DEFAULT_CATEGORIES))
    const [order, setOrder] = useState<string[]>([...DEFAULT_CATEGORIES])
    const [redditEnabled, setRedditEnabled] = useState(false)
    const [redditWeight, setRedditWeight] = useState(50)
    const dragSrc = useRef<number | null>(null)

    useEffect(() => { loadSources() }, [])

    useEffect(() => {
        setOrder(prev => {
            const kept = prev.filter(k => selectedCategories.has(k))
            const added = [...selectedCategories].filter(k => !prev.includes(k))
            return [...kept, ...added]
        })
    }, [selectedCategories])

    const loadSources = async () => {
        try { await sourcesApi.getFeatured({ page_size: 100 }) }
        catch (err) { console.error('Failed to load sources:', err) }
        finally { setIsLoading(false) }
    }

    const toggleCategory = (key: string) => {
        setSelectedCategories(prev => {
            const next = new Set(prev)
            if (next.has(key)) next.delete(key)
            else next.add(key)
            return next
        })
    }

    // Desktop drag
    const handleDragStart = (i: number) => { dragSrc.current = i }
    const handleDragOver = (e: React.DragEvent, i: number) => {
        e.preventDefault()
        if (dragSrc.current === null || dragSrc.current === i) return
        setOrder(prev => {
            const next = [...prev]
            const [moved] = next.splice(dragSrc.current!, 1)
            next.splice(i, 0, moved)
            dragSrc.current = i
            return next
        })
    }
    const handleDragEnd = () => { dragSrc.current = null }

    // Mobile reorder
    const moveItem = (i: number, dir: -1 | 1) => {
        const j = i + dir
        if (j < 0 || j >= order.length) return
        setOrder(prev => {
            const next = [...prev]
                ;[next[i], next[j]] = [next[j], next[i]]
            return next
        })
    }

    const handleContinue = async () => {
        if (selectedCategories.size === 0) return
        setIsSaving(true)
        try {
            const categoryPrefs: any = {}
            CATEGORIES.forEach(cat => { categoryPrefs[cat.key] = selectedCategories.has(cat.key) })
            const redditCategoryPrefs: any = {}
            CATEGORIES.forEach(cat => { redditCategoryPrefs[cat.key] = redditEnabled && selectedCategories.has(cat.key) })

            await preferencesApi.updateCategories({
                ...categoryPrefs,
                reddit_enabled: redditEnabled,
                reddit_weight: redditWeight,
                reddit_categories: redditCategoryPrefs,
                reddit_personal_enabled: false,
                briefing_style: 'conversational',
                format_style: 'prose',
                prose_paragraph_length: 'short',
                template_style: 'modern',
                briefing_tone: 'professional',
                max_articles_per_briefing: 50,
                max_articles_per_source: 10,
                enable_category_grouping: true,
                enable_story_integration: true,
                include_source_attribution: true,
                enable_cross_source_connections: true,
                keyword_boosts: [],
                keyword_blocks: [],
            })

            await settingsApi.update({
                market_config: {
                    enabled: false, include_top_crypto: false, include_top_stocks: false,
                    include_commodities: false, include_top_movers: false,
                    crypto_display_limit: 12, stock_display_limit: 10, commodities_display_limit: 4,
                    section_position: 'start', top_assets_count: 10, top_movers_count: 5,
                    cache_ttl_minutes: 30, include_indices: false, indices_display_limit: 5,
                    tracked_sectors: [], news_sentiment: false, respect_market_hours: true,
                    performance_timeframe: '24h', grouping_style: 'by_type',
                    price_alerts: false, alert_threshold_percent: 5,
                },
            })
            onNext()
        } catch (err: any) {
            console.error('Save failed:', err)
        } finally {
            setIsSaving(false)
        }
    }

    if (isLoading) {
        return (
            <div className="ob-body" style={{ alignItems: 'center', justifyContent: 'center', display: 'flex', flex: 1 }}>
                <div style={{ width: 24, height: 24, borderRadius: '50%', border: '2px solid #6600FF', borderTopColor: 'transparent', animation: 'spin 0.7s linear infinite' }} />
            </div>
        )
    }

    const orderedSelected = order.filter(k => selectedCategories.has(k))

    return (
        <>
            <div className="ob-body">
                <div className={`ob-eyebrow ${accentClass}`}>{eyebrow}</div>
                <h2 className="ob-heading">Your topics</h2>
                <p className="ob-sub">Select what matters. {isMobile ? 'Tap arrows to prioritize.' : 'Drag to prioritize.'}</p>

                {/* Topic pills */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
                    {CATEGORIES.map(cat => {
                        const on = selectedCategories.has(cat.key)
                        return (
                            <button
                                key={cat.key}
                                type="button"
                                onClick={() => toggleCategory(cat.key)}
                                style={{
                                    padding: '6px 13px', borderRadius: 999, fontSize: 12, cursor: 'pointer',
                                    border: `0.5px solid ${on ? `${cat.color}66` : 'var(--color-border-tertiary)'}`,
                                    color: on ? cat.color : 'var(--color-text-secondary)',
                                    background: on ? `${cat.color}0d` : 'var(--color-background-secondary)',
                                    transition: 'all 0.12s',
                                    // Bigger tap target on mobile
                                    minHeight: isMobile ? 36 : 'auto',
                                }}
                            >
                                {cat.label}
                            </button>
                        )
                    })}
                    {/* Reddit gated pills */}
                    {[{ label: 'Reddit home' }, { label: 'Reddit personal' }].map(r => (
                        <span key={r.label} style={{
                            padding: '6px 13px', borderRadius: 999, fontSize: 12,
                            border: '0.5px solid rgba(102,0,255,0.2)',
                            color: 'rgba(102,0,255,0.45)',
                            background: 'rgba(102,0,255,0.04)',
                            display: 'flex', alignItems: 'center', gap: 5,
                            minHeight: isMobile ? 36 : 'auto',
                        }}>
                            {r.label}
                            <span className="ob-pro-pip">PRO+</span>
                        </span>
                    ))}
                </div>

                {/* Priority list */}
                {orderedSelected.length > 0 && (
                    <div style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 10, color: 'var(--color-text-secondary)', marginBottom: 6, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                            Priority order
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {orderedSelected.map((key, i) => {
                                const cat = CATEGORIES.find(c => c.key === key)!
                                return (
                                    <div
                                        key={key}
                                        draggable={!isMobile}
                                        onDragStart={() => handleDragStart(i)}
                                        onDragOver={e => handleDragOver(e, i)}
                                        onDragEnd={handleDragEnd}
                                        className="ob-card-surface"
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: 9,
                                            padding: isMobile ? '10px 11px' : '8px 11px',
                                            cursor: isMobile ? 'default' : 'grab',
                                        }}
                                    >
                                        {!isMobile && (
                                            <span style={{ color: 'var(--color-text-secondary)', opacity: 0.3, fontSize: 13, flexShrink: 0 }} aria-hidden="true">⠿</span>
                                        )}
                                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: cat.color, flexShrink: 0 }} />
                                        <span style={{ fontSize: 12, fontWeight: 500, flex: 1 }}>{cat.label}</span>
                                        <span style={{ fontSize: 10, color: 'var(--color-text-secondary)', fontFamily: 'var(--font-mono, monospace)', marginRight: isMobile ? 4 : 0 }}>
                                            #{i + 1}
                                        </span>
                                        {isMobile && (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                <button
                                                    type="button" onClick={() => moveItem(i, -1)} disabled={i === 0}
                                                    style={{ background: 'none', border: 'none', padding: '2px 4px', cursor: i === 0 ? 'default' : 'pointer', opacity: i === 0 ? 0.2 : 0.6 }}
                                                    aria-label="Move up"
                                                >
                                                    <ChevronUp size={14} />
                                                </button>
                                                <button
                                                    type="button" onClick={() => moveItem(i, 1)} disabled={i === orderedSelected.length - 1}
                                                    style={{ background: 'none', border: 'none', padding: '2px 4px', cursor: i === orderedSelected.length - 1 ? 'default' : 'pointer', opacity: i === orderedSelected.length - 1 ? 0.2 : 0.6 }}
                                                    aria-label="Move down"
                                                >
                                                    <ChevronDown size={14} />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                )}

                {/* Reddit toggle */}
                <div className="ob-card-surface" style={{ padding: '10px 13px', marginBottom: 10 }}>
                    <div className="ob-topline ob-topline--teal" />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                        <div style={{
                            width: 22, height: 22, borderRadius: 5, flexShrink: 0,
                            background: 'rgba(255,87,30,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 11, fontWeight: 700, color: 'rgba(255,87,30,0.85)',
                        }}>r</div>
                        <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 12, fontWeight: 500 }}>Reddit discussions</div>
                            {redditEnabled && (
                                <div style={{ fontSize: 10, color: 'var(--color-text-secondary)', marginTop: 3, display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <input
                                        type="range" min={0} max={100} value={redditWeight} step={10}
                                        onChange={e => setRedditWeight(parseInt(e.target.value))}
                                        style={{ flex: 1, accentColor: '#FF5700' }}
                                    />
                                    <span style={{ color: 'rgba(255,87,30,0.8)', minWidth: 32 }}>{redditWeight}%</span>
                                </div>
                            )}
                        </div>
                        <button
                            type="button"
                            className={`ob-tog${redditEnabled ? '' : ' ob-tog--off'}`}
                            onClick={() => setRedditEnabled(v => !v)}
                            aria-label="Toggle Reddit"
                        />
                    </div>
                </div>

                {/* Gate bar */}
                <div className="ob-gate-bar">
                    <Lock size={13} color="#FF12C7" style={{ flexShrink: 0 }} />
                    <span className="ob-gate-text">Reddit personal feed unlocks with Pro+.</span>
                    <button className="ob-gate-cta" type="button">Upgrade →</button>
                </div>
            </div>

            <div className="ob-footer">
                <button type="button" className="ob-btn-back" onClick={onPrev}>
                    <ArrowLeft size={13} /> Back
                </button>
                <button
                    type="button"
                    className={`ob-btn-next ${btnClass}`}
                    onClick={handleContinue}
                    disabled={selectedCategories.size === 0 || isSaving}
                >
                    {isSaving ? 'Saving…' : 'Continue'} <ArrowRight size={13} />
                </button>
            </div>
        </>
    )
}